import React, { useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
    fillTemplateBody,
    getTemplateDisplayName,
    getTemplateVariableLabel,
    getTemplateVariables,
} from '../../lib/communicationsTemplates';
import { messageCharacterCounter } from '../../lib/signatures';
import { SupportWriteGuard, useSupportSession } from '../../context/SupportSessionContext';
import { EnterKeyPreferenceContext, resolveEnterKeyAction } from '../../lib/enterKeyPreference';
import ActionPillMenu from '../ui/ActionPillMenu';
import SplitButton from '../ui/SplitButton';
import { formatPhone } from '../../lib/phone';
import { composerSenderLine } from '../../lib/communicationsDisplay';
import SchedulePanel from './SchedulePanel';
import HoverHelp from '../shared/HoverHelp';
import SignatureChip from './SignatureChip';
import EmailRichEditor from './EmailRichEditor';
import './messageComposerResponsive.css';
import { usePinnedTemplatePreference } from '../../lib/pinnedTemplatePreference';
import { TEMPLATE_MENU_LIMIT, templateMenuEntries } from '../../lib/templateMenu';

export const MESSAGE_TEXTAREA_MIN_HEIGHT = 40;
export const MESSAGE_TEXTAREA_MAX_HEIGHT = 200;

export function firstNameOnly(value = '', fallback = 'customer') {
    const clean = typeof value === 'string' ? value.trim() : '';
    if (!clean) return fallback;
    return clean.split(/\s+/)[0] || fallback;
}

export function formatComposerAddress(address = '') {
    const trimmed = typeof address === 'string' ? address.trim() : '';
    // BL-177 Step 2d: THE phone helper — +1 (404) 594-2667, +49 163 5959331; emails come back unchanged.
    return formatPhone(trimmed);
}

/** "from +1 (404) 594-2667 · The Ride" (+ " · WhatsApp" on WhatsApp, as on board 07). */
export function formatComposerIdentity(identity = null, channel = '') {
    if (!identity?.address) return '';
    const line = composerSenderLine({ number: identity.address, companyName: identity.entityName || '' });
    const channelLine = channel === 'whatsapp' ? `${line} · WhatsApp` : line;
    return identity.isTestLine ? `${channelLine} · test line` : channelLine;
}

function MessageComposerBox({
    theme = 'light',
    composeKey = 'shared-composer',
    body = '',
    onBodyChange,
    bodyPlaceholder = 'Write a message…',
    bodyAriaLabel = 'Message body',
    channelNotice = '',
    selectedChannel = '',
    channelOptions = [],
    onChannelChange,
    showCampaignPill = false,
    campaignEnabled = false,
    onCampaignToggle,
    selectedIdentity = null,
    channelIdentities = [],
    selectedIdentityId = '',
    onIdentityChange,
    selectedTemplate = null,
    selectedTemplateSid = '',
    templateIncomplete = false,
    templates = [],
    onTemplateChoose,
    onTemplateRemove,
    onTemplatePanelRequestOpen,
    // Comms gate fixes: the WhatsApp sending account this conversation uses, so its Templates submenu lists only templates
    // registered on that account (as the side-panel picker does). undefined = not known, list every approved WhatsApp template.
    whatsappTemplateAccountSid = undefined,
    // Comms next pass: Templates → "Edit templates" opens Settings → Templates on this channel's tab. Called with the
    // channel; the host keeps the unsent draft. Without it the old inline panel opens.
    onEditTemplates,
    // Comms final pass (#759 follow-up): Settings → Templates is admin-only, so "Edit templates" shows for admins only.
    canEditTemplates = true,
    onUploadAttachment,
    onInsertText,
    subject = '',
    onSubjectChange,
    showSubject = false,
    subjectAriaLabel = 'Email subject',
    subjectPlaceholder = 'Subject',
    subjectHelp = null,
    channelHelp = null,
    // BL-177 Step 2m: the composer's "?" is the shared HoverHelp, so it obeys each user's own Self-Help off switch.
    userId = '',
    sending = false,
    disabled = false,
    sendDisabled = false,
    sendMenuDisabled = false,
    // BL-177 Step 2d: when Send is disabled, the plain-English reason printed under the composer (optional).
    sendDisabledReason = '',
    sendLabel = 'Send',
    onSend,
    templateVariables = {},
    onTemplateVariableChange,
    counterSignature = null,
    helpTitle = "WhatsApp: you can type freely for 24 hours after the customer's last message. After that, Meta only allows approved templates until they reply again.",
    helpAriaLabel = 'Message help',
    submitAriaLabel = '',
    senderOverrideLabel = '',
    // BL-177 Step 2n: the number an imported provider text goes out from, for the read-only "from ..." label (the picker is not used there).
    senderOverrideAddress = '',
    // BL-177 Step 2d: "Schedule send…" lives in the Send menu only once Step 3 ships. Off by default.
    scheduleSendEnabled = false,
    onScheduleSend,
    // BL-177 Step 3: the schedule panel lives here so both composers behave the same. `schedule` =
    // { enabled, companyTimeZone, recipientTimeZone, quietHours, busy, error, now, onConfirm({ at, cancelOnReply }) -> Promise<boolean> }.
    schedule = null,
    // BL-177 Step 2j (B2): the "Signature ✓" chip on the toolbar (see signatureChipFor in useMessageSignature.js). null = no chip.
    // (The dashed signature preview block under the box is gone.)
    signatureChip = null,
    // 1.12.212-beta: when Email is the chosen type — { to, cc, bcc, onCcChange, onBccChange, ccError, bccError, html, onHtmlChange }.
    // Cc / Bcc sit behind "Cc" / "Bcc" links on the To line (Outlook-style); `html` + `onHtmlChange` turn the reply box into the
    // taller formatted editor with its formatting bar. null for SMS / WhatsApp (and the box is the plain textarea, as before).
    email = null,
}) {
    const dark = theme === 'dark';
    const multipleIdentities = channelIdentities.length > 1;
    const textareaRef = useRef(null);
    const uploadRef = useRef(null);
    const composerSurface=useRef(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [sendMenuOpen, setSendMenuOpen] = useState(false);
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [messageTypeMenuOpen, setMessageTypeMenuOpen] = useState(false);
    const pinnedTemplatePreference = usePinnedTemplatePreference(userId);
    const [ccShown, setCcShown] = useState(false);
    const [bccShown, setBccShown] = useState(false);
    const ccInputRef = useRef(null);
    const bccInputRef = useRef(null);
    const showCc = Boolean(email) && (ccShown || Boolean(email?.cc));
    const showBcc = Boolean(email) && (bccShown || Boolean(email?.bcc));
    const richEmail = Boolean(email && typeof email.onHtmlChange === 'function' && !selectedTemplate);
    const revealCopyField = (which) => {
        if (which === 'cc') setCcShown(true); else setBccShown(true);
        setTimeout(() => (which === 'cc' ? ccInputRef : bccInputRef).current?.focus(), 0);
    };
    const composedBody = selectedTemplate
        ? fillTemplateBody(selectedTemplate.body || selectedTemplate.types?.['twilio/text']?.body || '', templateVariables)
        : body;
    const resizeTextarea = (target = textareaRef.current) => {
        const node = target || textareaRef.current;
        if (!node) return;
        node.style.height = `${MESSAGE_TEXTAREA_MIN_HEIGHT}px`;
        node.style.height = `${Math.min(Math.max(node.scrollHeight, MESSAGE_TEXTAREA_MIN_HEIGHT), MESSAGE_TEXTAREA_MAX_HEIGHT)}px`;
    };

    useEffect(() => {
        resizeTextarea();
    }, [body, composedBody]);

    const whatsappTemplatesAvailable = Boolean(
        channelOptions.some((option) => option.value === 'whatsapp' && !option.disabled)
        && !sending && !disabled,
    );
    const channelReasonText = (option) => (!option.reason ? '' : (String(option.reason).toLowerCase().startsWith(`${String(option.label).toLowerCase()}:`) ? option.reason : `${option.label}: ${option.reason}`));

    const closeMenus = () => {
        setMenuOpen(false);
        setMessageTypeMenuOpen(false);
    };

    // Only one composer menu at a time: opening one closes the other.
    const handlePlusMenuOpenChange = (next) => {
        setMenuOpen(next);
        if (next) setMessageTypeMenuOpen(false);
    };

    const handleMessageTypeMenuOpenChange = (next) => {
        setMessageTypeMenuOpen(next);
        if (next) setMenuOpen(false);
    };

    // BL-177 Step 2 Part B: the person's "When I press Enter" choice. Chat channels only; email keeps
    // Enter = line break. The Send button always works, and a send is never started while a send is
    // already running, the composer is blocked, the message is empty, or an IME is composing.
    const enterKeyMode = useContext(EnterKeyPreferenceContext);
    const { isSupportMode } = useSupportSession();
    const handleBodyKeyDown = (event) => {
        const action = resolveEnterKeyAction({
            key: event.key,
            shiftKey: event.shiftKey,
            ctrlKey: event.ctrlKey,
            metaKey: event.metaKey,
            altKey: event.altKey,
            isComposing: event.nativeEvent?.isComposing ?? event.isComposing,
            keyCode: event.keyCode,
        }, { mode: enterKeyMode, channel: selectedChannel });
        if (action !== 'send') return;
        event.preventDefault();
        if (sending || disabled || sendDisabled || isSupportMode || !body.trim()) return;
        onSend?.();
    };

    const handleBodyInput = (event) => {
        const nextBody = event.target.value;
        resizeTextarea(event.target);
        if (nextBody.startsWith('/') && selectedChannel === 'whatsapp' && whatsappTemplatesAvailable) {
            setMessageTypeMenuOpen(true);
            onBodyChange?.(nextBody.slice(1));
            return;
        }
        onBodyChange?.(nextBody);
        if (!nextBody.trim() && selectedChannel === 'whatsapp') setMessageTypeMenuOpen(false);
    };

    // Locked hotfix: pinned choices (max five) sit directly above Edit templates.
    const templateOptionsFor = (channel) => {
        const { items } = templateMenuEntries({
            templates,
            channel,
            pinnedIds: pinnedTemplatePreference.ids,
            limit: TEMPLATE_MENU_LIMIT,
            whatsappAccountSid: channel === 'whatsapp' ? whatsappTemplateAccountSid : undefined,
        });
        return [
            ...items.map(({ template, pinned }) => {
                const name = getTemplateDisplayName(template);
                return {
                    key: template.sid,
                    // the pin is part of the label (an option "icon" would collapse the name to an icon until hover)
                    label: pinned
                        ? <><span aria-hidden="true" className="ia-comms-template-pin">{'\u{1F4CC}'}</span><span className="sr-only">Pinned: </span>{name}</>
                        : name,
                    title: pinned ? `${name} (pinned)` : name,
                    className: pinned ? 'ia-comms-template-option ia-comms-template-option--pinned' : 'ia-comms-template-option',
                    onSelect: () => {
                        if (selectedChannel !== channel) onChannelChange?.(channel);
                        onTemplateChoose?.(template);
                        closeMenus();
                    },
                };
            }),
            // nothing to list and nothing to edit (not an admin): say so rather than open an empty submenu
            ...(items.length === 0 && !canEditTemplates ? [{
                key: `none-${channel}-templates`,
                label: 'No templates for this channel yet',
                displayOnly: true,
            }] : []),
            ...(canEditTemplates ? [{
                key: `edit-${channel}-templates`,
                label: 'Edit templates',
                onSelect: () => {
                    closeMenus();
                    if (onEditTemplates) onEditTemplates(channel);
                    else onTemplatePanelRequestOpen?.('edit', channel);
                },
            }] : []),
        ];
    };

    const messageTypeOptions = channelOptions.filter((option) => !option.hidden && !option.disabled).map((option) => ({
        key: option.value,
        label: option.label,
        title: channelReasonText(option),
        disabled: option.disabled,
        active: selectedChannel===option.value,
        className:'ia-message-channel-option',
        onSelect:()=>{
            onChannelChange?.(option.value);
        },
        options: templateOptionsFor(option.value),
    }));

    const plusOptions = [
        ...(onUploadAttachment ? [{ key: 'upload', label: 'Photo or file', onSelect: () => uploadRef.current?.click() }] : []),
        { key: 'tripDetails', label: 'Share trip details', onSelect: () => onInsertText?.('tripDetails') },
        { key: 'location', label: 'Location', onSelect: () => onInsertText?.('location') },
    ];

    const sendBlocked = sending || disabled || sendDisabled || isSupportMode;
    const showSenderPicker = Boolean(!senderOverrideLabel && multipleIdentities);
    const sendOptions = [
        ...(showSenderPicker ? channelIdentities.map((identity) => ({
            key: `line-${identity.id}`,
            label: formatComposerIdentity(identity, selectedChannel),
            active: identity.id === (selectedIdentityId || selectedIdentity?.id),
            disabled: Boolean(identity.blockedReason),
            title: identity.blockedReason || formatComposerIdentity(identity, selectedChannel),
            onSelect: () => onIdentityChange?.(identity.id),
        })) : []),
        ...((scheduleSendEnabled || schedule?.enabled) ? [{
            key: 'schedule',
            label: 'Schedule send…',
            disabled: sending || disabled || isSupportMode,
            onSelect: () => {
                if (schedule?.enabled) setScheduleOpen(true);
                onScheduleSend?.();
            },
        }] : []),
        { key: 'send-now', label: 'Send now', disabled: sendBlocked, onSelect: () => onSend?.() },
    ];
    const characterCounter = messageCharacterCounter(composedBody, counterSignature, selectedChannel);
    const selectedVariableKeys = selectedTemplate ? Object.keys(getTemplateVariables(selectedTemplate)) : [];
    // Comms final pass: after a template is inserted, every value it could not fill on its own is highlighted for staff to type.
    const emptyVariableKeys = selectedVariableKeys.filter((key) => !String(templateVariables[key] ?? '').trim());
    const emptyNoteId = useId();

    return (
        <div ref={composerSurface}
            className="relative rounded-2xl border border-[var(--surface-border)] bg-[var(--surface-raised)] p-2 text-[var(--ink)]"
            style={{ borderRadius: 16 }}
            data-shared-message-composer="true"
        >
            {email && (
                <div className="ia-email-recipients" data-email-recipients="true">
                    <div className="ia-email-recipients__row" data-email-to="true">
                        <span className="ia-email-recipients__label">To</span>
                        <span className="min-w-0 truncate" title={email.to || ''}>{email.to || 'No email address on file'}</span>
                        <span className="ia-email-recipients__links">
                            {!showCc && (
                                <button type="button" className="ia-email-recipients__link" onClick={() => revealCopyField('cc')} disabled={sending || disabled} aria-label="Add Cc recipients">Cc</button>
                            )}
                            {!showBcc && (
                                <button type="button" className="ia-email-recipients__link" onClick={() => revealCopyField('bcc')} disabled={sending || disabled} aria-label="Add Bcc recipients">Bcc</button>
                            )}
                        </span>
                    </div>
                    {showCc && (
                        <label className="ia-email-recipients__row" data-email-cc-row="true">
                            <span className="ia-email-recipients__label">Cc</span>
                            <input
                                ref={ccInputRef}
                                type="text"
                                inputMode="email"
                                autoComplete="email"
                                value={email.cc || ''}
                                onChange={(event) => email.onCcChange?.(event.target.value)}
                                disabled={sending || disabled}
                                placeholder="name@example.com, another@example.com"
                                aria-label="Cc recipients"
                                aria-invalid={email.ccError ? 'true' : undefined}
                                aria-describedby={email.ccError ? `${composeKey}-cc-error` : undefined}
                            />
                        </label>
                    )}
                    {email.ccError && <p id={`${composeKey}-cc-error`} className="ia-email-recipients__error" role="alert">{email.ccError}</p>}
                    {showBcc && (
                        <label className="ia-email-recipients__row" data-email-bcc-row="true">
                            <span className="ia-email-recipients__label">Bcc</span>
                            <input
                                ref={bccInputRef}
                                type="text"
                                inputMode="email"
                                autoComplete="email"
                                value={email.bcc || ''}
                                onChange={(event) => email.onBccChange?.(event.target.value)}
                                disabled={sending || disabled}
                                placeholder="Hidden copies — only your team sees these"
                                aria-label="Bcc recipients"
                                aria-invalid={email.bccError ? 'true' : undefined}
                                aria-describedby={email.bccError ? `${composeKey}-bcc-error` : undefined}
                            />
                        </label>
                    )}
                    {email.bccError && <p id={`${composeKey}-bcc-error`} className="ia-email-recipients__error" role="alert">{email.bccError}</p>}
                </div>
            )}
            {showSubject && (
                <div className="ia-email-subject-row mb-2 flex items-center gap-1">
                    <input
                        type="text"
                        value={subject}
                        onChange={(event) => onSubjectChange?.(event.target.value)}
                        disabled={sending || disabled}
                        placeholder={subjectPlaceholder}
                        aria-label={subjectAriaLabel}
                        className="ia-email-subject-input w-full border-b border-[var(--surface-border)] bg-transparent px-2 py-1.5 text-xs text-[var(--ink)] outline-none placeholder:text-[var(--ink-muted)]"
                    />
                    {subjectHelp}
                </div>
            )}
            {selectedTemplate && (
                <div className="mb-2 flex min-w-0 items-center gap-1 rounded-full border border-[#03B7C9]/50 px-2 py-1 text-xs text-[#03B7C9]">
                    <span className="shrink-0">Template:</span>
                    <button
                        type="button"
                        onClick={() => onTemplatePanelRequestOpen?.('selected')}
                        className="min-w-0 truncate text-left"
                        title={getTemplateDisplayName(selectedTemplate)}
                    >
                        “{getTemplateDisplayName(selectedTemplate)}”
                    </button>
                    <button type="button" onClick={() => onTemplateRemove?.()} aria-label="Remove selected template" className="ml-auto shrink-0 px-1">×</button>
                </div>
            )}
            {selectedTemplate && selectedVariableKeys.length > 0 && (
                <div className="mb-2" data-template-fill-ins="true">
                    {emptyVariableKeys.length > 0 && (
                        <p id={emptyNoteId} className="ia-template-empty-note mb-1 text-xs font-semibold" role="status" data-template-empty-count={emptyVariableKeys.length}>
                            {emptyVariableKeys.length === 1 ? '1 value to fill in before sending' : `${emptyVariableKeys.length} values to fill in before sending`}
                        </p>
                    )}
                    <div className="grid gap-2 sm:grid-cols-2">
                        {selectedVariableKeys.map((key, index) => {
                            const label = getTemplateVariableLabel(selectedTemplate, key, index);
                            const empty = !String(templateVariables[key] ?? '').trim();
                            return (
                                <label key={key} className="min-w-0 text-xs">
                                    <span className="mb-1 block font-medium text-[var(--ink-muted)]">{label}</span>
                                    <input
                                        type="text"
                                        value={String(templateVariables[key] ?? '')}
                                        onChange={(event) => onTemplateVariableChange?.(key, event.target.value)}
                                        disabled={sending || disabled}
                                        aria-label={`Template value ${label}`}
                                        aria-invalid={empty ? 'true' : undefined}
                                        aria-describedby={empty ? emptyNoteId : undefined}
                                        data-template-value-empty={empty ? 'true' : undefined}
                                        className={`w-full rounded-lg border border-[var(--surface-border)] bg-transparent px-2 py-1 text-sm text-[var(--ink)]${empty ? ' ia-template-empty' : ''}`}
                                    />
                                </label>
                            );
                        })}
                    </div>
                </div>
            )}
            {richEmail ? (
                <div className="relative" data-email-body="formatted">
                    <EmailRichEditor
                        value={email.html || ''}
                        actions={email.actions}
                        onChange={email.onHtmlChange}
                        onKeyDown={handleBodyKeyDown}
                        placeholder={bodyPlaceholder}
                        ariaLabel={bodyAriaLabel}
                        disabled={sending || disabled}
                    />
                </div>
            ) : (
            <div className="relative">
                <textarea
                    ref={textareaRef}
                    value={composedBody}
                    onChange={handleBodyInput}
                    onKeyDown={handleBodyKeyDown}
                    disabled={sending || disabled}
                    readOnly={Boolean(selectedTemplate)}
                    rows={1}
                    placeholder={bodyPlaceholder}
                    aria-label={bodyAriaLabel}
                    className="w-full resize-none overflow-y-auto bg-transparent px-1 py-2 pb-5 pr-24 text-sm text-[var(--ink)] outline-none placeholder:text-[var(--ink-muted)]"
                    style={{ minHeight: MESSAGE_TEXTAREA_MIN_HEIGHT, maxHeight: MESSAGE_TEXTAREA_MAX_HEIGHT }}
                />
                {characterCounter && (
                    <span className="ia-composer-counter" data-character-counter="true" aria-live="polite">
                        {characterCounter}
                    </span>
                )}
            </div>
            )}
            <p
                className={channelNotice ? 'border-t border-[var(--surface-border)] px-2 py-1 text-center text-xs text-[var(--ink-muted)]' : 'sr-only'}
                role="status"
                aria-live="polite"
                data-channel-notice="true"
            >
                {channelNotice}
            </p>
            <div className="ia-composer-toolbar flex min-w-0 flex-nowrap items-center gap-1 overflow-visible px-1 pt-1" data-composer-toolbar="true">
                <div className="relative flex-none">
                    <ActionPillMenu
                        menuLabel="Reply actions"
                        options={plusOptions}
                        placement="top"
                        open={menuOpen}
                        onOpenChange={handlePlusMenuOpenChange}
                        triggerLabel="More reply options"
                        // The "+" stays transparent at rest with theme-aware ink. On touch, only
                        // the expanded state paints its turquoise pseudo-element.
                        triggerClassName={({ open }) => `communications-plus-button inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full text-lg font-semibold text-[#0B1220] transition-all ${open ? 'bg-[#03B7C9]' : 'bg-transparent'}`}
                    >
                        +
                    </ActionPillMenu>
                    {onUploadAttachment && (
                        <input
                            ref={uploadRef}
                            id={`composer-upload-${composeKey}`}
                            type="file"
                            accept="image/jpeg,image/png,image/gif,application/pdf"
                            className="hidden"
                            onChange={onUploadAttachment}
                        />
                    )}
                </div>
                <div className="flex min-w-0 flex-none items-center gap-1" role="group" aria-label="Delivery channel">
                    <ActionPillMenu
                        menuLabel="Select message type"
                        options={messageTypeOptions}
                        menuClassName="ia-message-type-menu"
                        placement="top"
                        mobilePlacement="top"
                        open={messageTypeMenuOpen}
                        onOpenChange={handleMessageTypeMenuOpenChange}
                        initialFocusKey={messageTypeOptions.find((option) => !option.disabled)?.key}
                        disabled={sending || disabled || messageTypeOptions.length === 0}
                        wrapperProps={{ 'data-message-type-select': 'true' }}
                        triggerLabel={selectedChannel
                            ? `${channelOptions.find((option) => option.value === selectedChannel)?.label || selectedChannel} message type`
                            : 'Select message type'}
                        triggerClassName={`ia-btn3d ia-comms-pill ${selectedChannel ? 'ia-btn3d--go ia-comms-pill--on' : 'ia-btn3d--primary'}`}
                    >
                        {channelOptions.find((option) => option.value === selectedChannel)?.label || 'Select'}
                    </ActionPillMenu>
                </div>
                {showCampaignPill && (
                    <button
                        type="button"
                        onClick={() => onCampaignToggle?.(!campaignEnabled)}
                        disabled={sending || disabled}
                        aria-pressed={campaignEnabled}
                        className={`ia-btn3d ia-comms-pill ${campaignEnabled ? 'ia-btn3d--go ia-comms-pill--on' : ''}`}
                    >
                        Campaign
                    </button>
                )}
                {signatureChip && <SignatureChip chip={signatureChip} />}
                <span className="flex-none">
                    {channelHelp || (
                        <HoverHelp helpId="communications.reply-channel" content={{ title: 'Replying', body: helpTitle }} label={helpAriaLabel} userId={userId} />
                    )}
                </span>
                <div className="ml-auto flex min-w-0 shrink-0 items-center gap-1" data-sender-group="true">
                {/* BL-177 Step 2d: Send is a split button. The menu shows "Send now"; "Schedule send…" only once Step 3 ships. */}
                <div className="relative inline-flex shrink-0" data-send-group="true" data-send-split="true">
                    <SupportWriteGuard mode="disable" tooltip="Sending is disabled in a read-only support session">
                        <SplitButton
                            variant="go"
                            className="ia-send-split"
                            primaryLabel={sending ? 'Sending…' : sendLabel}
                            primaryAriaLabel={submitAriaLabel || sendLabel}
                            primaryType="submit"
                            primaryDisabled={sendBlocked}
                            onPrimaryClick={(event) => {
                                event.preventDefault();
                                setSendMenuOpen(false);
                                onSend?.();
                            }}
                            menuOpen={sendMenuOpen}
                            onMenuOpenChange={setSendMenuOpen}
                            menuLabel="Send options"
                            menuTriggerLabel="More send options"
                            secondaryAriaLabel="More send options"
                            secondaryDisabled={sending || disabled || isSupportMode || sendMenuDisabled}
                            menuOptions={sendOptions}
                        />
                    </SupportWriteGuard>
                    {scheduleOpen && schedule?.enabled && (
                        <div className="absolute bottom-full right-0 z-40 mb-2">
                            <SchedulePanel
                                now={schedule.now}
                                companyTimeZone={schedule.companyTimeZone}
                                recipientTimeZone={schedule.recipientTimeZone}
                                quietHours={schedule.quietHours}
                                busy={schedule.busy}
                                error={schedule.error}
                                onClose={() => setScheduleOpen(false)}
                                onConfirm={async (choice) => {
                                    const done = await schedule.onConfirm?.(choice);
                                    if (done !== false) setScheduleOpen(false);
                                }}
                            />
                        </div>
                    )}
                </div>
                </div>
            </div>
            {!sending && sendDisabledReason && (
                <p className="px-1 pt-1.5 text-right text-xs text-white" data-send-disabled-reason="true" role="alert">{sendDisabledReason}</p>
            )}
            {pinnedTemplatePreference.error && (
                <p className="px-1 pt-1.5 text-xs text-red-500" role="alert">{pinnedTemplatePreference.error}</p>
            )}
        </div>
    );
}

export default MessageComposerBox;
